import React, { useState, useEffect, useCallback } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import {
  XMarkIcon,
  ExclamationTriangleIcon,
  ArrowRightIcon,
  ArchiveBoxIcon,
  DocumentDuplicateIcon,
  UserIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { DocumentListItem } from '../DocumentAPI';

interface ArchiveDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  document: DocumentListItem;
  onArchive: (data: ArchiveDocumentData) => Promise<void>;
  onTransfer: (data: TransferDocumentData) => Promise<void>;
}

interface ArchiveDocumentData {
  archive_reason: string;
  archive_notes: string;
  keep_visible: boolean; // Whether to keep document visible in main list
}

interface TransferDocumentData {
  to_user_id: string;
  transfer_reason: string;
  transfer_notes: string;
  client_notified: boolean;
}

interface User {
  user_id: string;
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role?: string;
}

const DOCUMENT_ARCHIVE_REASONS = [
  { value: 'case_completed', label: 'Case Completed' },
  { value: 'superseded_version', label: 'Superseded by New Version' },
  { value: 'client_requested', label: 'Client Requested Removal' },
  { value: 'outdated_content', label: 'Outdated Content' },
  { value: 'duplicate_document', label: 'Duplicate Document' },
  { value: 'other', label: 'Other' }
];

const DOCUMENT_TRANSFER_REASONS = [
  { value: 'workload_redistribution', label: 'Workload Redistribution' },
  { value: 'specialty_expertise', label: 'Specialist Review Required' },
  { value: 'matter_reassignment', label: 'Matter Reassignment' },
  { value: 'attorney_departure', label: 'Attorney Departure' },
  { value: 'collaboration_request', label: 'Collaboration Request' },
  { value: 'other', label: 'Other' }
];

const ArchiveDocumentModal: React.FC<ArchiveDocumentModalProps> = ({
  isOpen,
  onClose,
  document,
  onArchive,
  onTransfer
}) => {
  const { keycloak } = useKeycloak();
  const [selectedOption, setSelectedOption] = useState<'archive' | 'transfer' | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  
  // Archive form state
  const [archiveReason, setArchiveReason] = useState('');
  const [archiveNotes, setArchiveNotes] = useState('');
  const [keepVisible, setKeepVisible] = useState(false);
  
  // Transfer form state
  const [selectedUser, setSelectedUser] = useState('');
  const [transferReason, setTransferReason] = useState('');
  const [transferNotes, setTransferNotes] = useState('');
  const [clientNotified, setClientNotified] = useState(false);
  const [showAssignConfirmation, setShowAssignConfirmation] = useState(false);

  // Get JWT token from Keycloak (following working pattern)
  const getAuthToken = useCallback(() => {
    console.log('🔑 [ArchiveDocumentModal] Getting token from React Keycloak provider...');
    
    // 1. First try the React Keycloak provider (most reliable)
    if (keycloak?.token) {
      console.log('✅ [ArchiveDocumentModal] Found token via React Keycloak provider');
      return keycloak.token;
    }
    
    // 2. Fall back to storage-based detection (same pattern as other working components)
    const jwtPattern = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;
    
    console.log('🔍 [ArchiveDocumentModal] Falling back to storage detection...');
    
    // Try localStorage first
    const keys = ['access_token', 'webelieve_access_token', 'token', 'authToken', 'jwt_token'];
    for (const key of keys) {
      const stored = localStorage.getItem(key);
      if (stored && jwtPattern.test(stored)) {
        console.log(`✅ [ArchiveDocumentModal] Found valid token in localStorage.${key}`);
        return stored;
      }
    }
    
    // Try sessionStorage
    for (const key of keys) {
      const stored = sessionStorage.getItem(key);
      if (stored && jwtPattern.test(stored)) {
        console.log(`✅ [ArchiveDocumentModal] Found valid token in sessionStorage.${key}`);
        return stored;
      }
    }
    
    console.error('❌ [ArchiveDocumentModal] No valid JWT token found');
    return null;
  }, [keycloak?.token]);

  // Standard auth headers following working pattern
  const getAuthHeaders = useCallback(() => {
    const token = getAuthToken();
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }, [getAuthToken]);

  // Load users for transfer functionality (replicating attorney loading pattern)
  const loadUsers = useCallback(async () => {
    console.log('🔍 [ArchiveDocumentModal] Loading users for transfer...');
    setLoadingUsers(true);
    
    try {
      // Multiple endpoints to try (following same pattern as CloseMatterModal)
      const endpoints = [
        'https://app.webelievesoftware.com/api/v1/dms/users',
        'https://app.webelievesoftware.com/api/v1/admin/users',
        'https://app.webelievesoftware.com/api/v1/users'
      ];
      
      let userList: User[] = [];
      
      for (const endpoint of endpoints) {
        try {
          console.log(`🔍 [ArchiveDocumentModal] Trying ${endpoint}...`);
          
          const response = await fetch(endpoint, {
            headers: getAuthHeaders()
          });
          
          if (response.ok) {
            const data = await response.json();
            console.log(`✅ [ArchiveDocumentModal] ${endpoint} returned:`, data);
            
            const users = data.users || data.data || data || [];
            
            if (Array.isArray(users)) {
              // Process users similar to attorney processing
              userList = users.map((user: any) => {
                let firstName = user.first_name || user.firstName || 'Unknown';
                let lastName = user.last_name || user.lastName || 'User';
                
                // Handle cases where name might be in email
                if ((!firstName || firstName === 'Unknown') && user.email) {
                  const emailName = user.email.split('@')[0];
                  firstName = emailName.charAt(0).toUpperCase() + emailName.slice(1);
                  lastName = '';
                }
                
                return {
                  user_id: user.user_id || user.id || user.keycloak_user_id,
                  id: user.id || user.user_id || user.keycloak_user_id,
                  first_name: firstName,
                  last_name: lastName,
                  email: user.email || 'no-email@example.com',
                  role: user.role || 'user'
                };
              });
            } else {
              // Filter for active users from general user endpoints
              userList = users
                .filter((user: any) => 
                  user.role === 'attorney' || 
                  user.role === 'paralegal' ||
                  user.role === 'admin' ||
                  user.email?.includes('user') ||
                  (user.is_active !== false && user.user_id)
                )
                .map((user: any) => ({
                  user_id: user.user_id || user.id || user.keycloak_user_id,
                  id: user.id || user.user_id || user.keycloak_user_id,
                  first_name: user.first_name || user.firstName || 'Unknown',
                  last_name: user.last_name || user.lastName || 'User',
                  email: user.email || 'no-email@example.com',
                  role: user.role || 'user'
                }));
            }
            
            console.log(`🔍 [ArchiveDocumentModal] Processed ${userList.length} users from ${endpoint}`);
            
            if (userList.length > 0) break;
          } else {
            console.warn(`❌ [ArchiveDocumentModal] ${endpoint} returned ${response.status}`);
          }
        } catch (error) {
          console.warn(`Failed to load from ${endpoint}:`, error);
        }
      }
      
      console.log(`✅ [ArchiveDocumentModal] Final user list:`, userList);
      setUsers(userList);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoadingUsers(false);
    }
  }, [getAuthHeaders]);

  // Load users when transfer option is selected
  useEffect(() => {
    if (selectedOption === 'transfer' && users.length === 0 && keycloak?.authenticated) {
      loadUsers();
    }
  }, [selectedOption, keycloak?.authenticated, loadUsers]);

  const resetForm = () => {
    setSelectedOption(null);
    setArchiveReason('');
    setArchiveNotes('');
    setKeepVisible(false);
    setSelectedUser('');
    setTransferReason('');
    setTransferNotes('');
    setClientNotified(false);
    setShowAssignConfirmation(false);
    setIsSubmitting(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleArchiveSubmit = async () => {
    if (!archiveReason) return;
    
    setIsSubmitting(true);
    try {
      await onArchive({
        archive_reason: archiveReason,
        archive_notes: archiveNotes,
        keep_visible: keepVisible
      });
      handleClose();
    } catch (error) {
      console.error('Archive failed:', error);
      setIsSubmitting(false);
    }
  };

  const handleTransferSubmit = async () => {
    console.log('🔍 [ArchiveDocumentModal] Transfer submit clicked');
    console.log('🔍 [ArchiveDocumentModal] Transfer form data:', {
      selectedUser,
      transferReason,
      transferNotes,
      clientNotified
    });
    
    if (!selectedUser || !transferReason) {
      console.error('❌ [ArchiveDocumentModal] Missing required fields:', { selectedUser, transferReason });
      return;
    }

    // Check if trying to transfer to the same user
    if (document.created_by_user_id && document.created_by_user_id === selectedUser) {
      alert('Cannot transfer document to the same user it is already assigned to. Please select a different user.');
      return;
    }

    // Check if document is unassigned and show confirmation
    if (!document.created_by_user_id) {
      console.log('🔍 [ArchiveDocumentModal] Document is unassigned, showing confirmation');
      setShowAssignConfirmation(true);
      return;
    }
    
    console.log('🔍 [ArchiveDocumentModal] Document is assigned, proceeding with transfer');
    console.log('🔍 [ArchiveDocumentModal] Current document.created_by_user_id:', document.created_by_user_id);
    
    // Proceed with transfer for assigned documents
    await performTransfer();
  };

  const performTransfer = async () => {
    setIsSubmitting(true);
    try {
      console.log('🔍 [ArchiveDocumentModal] Calling onTransfer...');
      await onTransfer({
        to_user_id: selectedUser,
        transfer_reason: transferReason,
        transfer_notes: transferNotes,
        client_notified: clientNotified
      });
      console.log('✅ [ArchiveDocumentModal] Transfer completed successfully');
      handleClose();
    } catch (error) {
      console.error('❌ [ArchiveDocumentModal] Transfer failed:', error);
      setIsSubmitting(false);
    }
  };

  const handleConfirmAssignment = async () => {
    setShowAssignConfirmation(false);
    await performTransfer();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-full p-4">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity lex-overlay-enter"
          onClick={handleClose}
        />

        {/* Modal panel */}
        <div className="relative w-full max-w-lg bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl lex-modal-enter">
          {/* Header */}
          <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                Archive Document
              </h3>
              <button
                onClick={handleClose}
                className="bg-white dark:bg-gray-800 rounded-md text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-lex-accent"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Document Info */}
            <div className="bg-gray-50 dark:bg-gray-700 rounded-md p-4 mb-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <DocumentDuplicateIcon className="h-8 w-8 text-lex-accent" />
                </div>
                <div className="ml-4">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                    {document.title}
                  </h4>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {document.client_name && `Client: ${document.client_name}`}
                    {document.matter_description && ` • Matter: ${document.matter_description}`}
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500">
                    Created: {new Date(document.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>

            {/* Options Selection */}
            {!selectedOption && (
              <div>
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-4">
                  What would you like to do with this document?
                </h4>
                
                <div className="space-y-3">
                  {/* Archive Option */}
                  <button
                    onClick={() => setSelectedOption('archive')}
                    className="w-full p-4 text-left border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors group"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <ArchiveBoxIcon className="h-8 w-8 text-lex-accent mr-3" />
                        <div>
                          <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                            Archive Document
                          </h4>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Mark as completed and move to archived documents
                          </p>
                        </div>
                      </div>
                      <ArrowRightIcon className="h-5 w-5 text-gray-400 group-hover:text-gray-600" />
                    </div>
                  </button>

                  {/* Transfer Option */}
                  <button
                    onClick={() => setSelectedOption('transfer')}
                    className="w-full p-4 text-left border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors group"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <UserIcon className="h-8 w-8 text-green-500 mr-3" />
                        <div>
                          <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                            Transfer Document
                          </h4>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Transfer to another user
                          </p>
                        </div>
                      </div>
                      <ArrowRightIcon className="h-5 w-5 text-gray-400 group-hover:text-gray-600" />
                    </div>
                  </button>
                </div>
              </div>
            )}

            {/* Archive Form */}
            {selectedOption === 'archive' && (
              <div className="space-y-4">
                <div className="flex items-center mb-4">
                  <button
                    onClick={() => setSelectedOption(null)}
                    className="mr-3 text-gray-400 hover:text-gray-600"
                  >
                    ←
                  </button>
                  <ArchiveBoxIcon className="h-6 w-6 text-lex-accent mr-2" />
                  <h4 className="text-lg font-medium text-gray-900 dark:text-white">
                    Archive Document
                  </h4>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Archive Reason <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={archiveReason}
                    onChange={(e) => setArchiveReason(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-lex-accent focus:border-lex-accent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Select a reason...</option>
                    {DOCUMENT_ARCHIVE_REASONS.map(reason => (
                      <option key={reason.value} value={reason.value}>
                        {reason.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Archive Notes
                  </label>
                  <textarea
                    value={archiveNotes}
                    onChange={(e) => setArchiveNotes(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-lex-accent focus:border-lex-accent dark:bg-gray-700 dark:text-white"
                    placeholder="Additional notes about archiving this document..."
                  />
                </div>

                {/* Document Visibility Option */}
                <div className="bg-lex-accent-soft border border-lex-accent rounded-md p-4">
                  <div className="flex items-start">
                    <ExclamationTriangleIcon className="h-5 w-5 text-lex-accent mr-3 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-lex-accent mb-3">
                        Show Archived Documents?
                      </h4>
                      <p className="text-sm text-lex-accent mb-4">
                        Would you like this archived document to remain visible in your main documents list?
                      </p>
                      
                      <div className="space-y-2">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="visibility"
                            checked={keepVisible === true}
                            onChange={() => setKeepVisible(true)}
                            className="form-radio h-4 w-4 text-lex-accent focus:ring-lex-accent border-gray-300 dark:border-gray-600"
                          />
                          <span className="ml-2 text-sm text-lex-accent">
                            <strong>Yes</strong> - Keep visible in documents list (shows as "Archived" status)
                          </span>
                        </label>
                        
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="visibility"
                            checked={keepVisible === false}
                            onChange={() => setKeepVisible(false)}
                            className="form-radio h-4 w-4 text-lex-accent focus:ring-lex-accent border-gray-300 dark:border-gray-600"
                          />
                          <span className="ml-2 text-sm text-lex-accent">
                            <strong>No</strong> - Hide from documents list (only visible in Document Management)
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-md p-3">
                  <div className="flex">
                    <ClockIcon className="h-5 w-5 text-yellow-400 mr-2 mt-0.5" />
                    <div className="text-sm text-yellow-700 dark:text-yellow-300">
                      <p className="font-medium">Archive Impact:</p>
                      <ul className="mt-1 ml-2 list-disc text-xs">
                        <li>Document will be moved to Archived Documents section in Document Management</li>
                        {keepVisible ? (
                          <li>Document will remain visible in main documents list with "Archived" status</li>
                        ) : (
                          <li>Document will be hidden from main documents list</li>
                        )}
                        <li>Can be restored from Document Management if needed</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Transfer Form */}
            {selectedOption === 'transfer' && (
              <div className="space-y-4">
                <div className="flex items-center mb-4">
                  <button
                    onClick={() => setSelectedOption(null)}
                    className="mr-3 text-gray-400 hover:text-gray-600"
                  >
                    ←
                  </button>
                  <UserIcon className="h-6 w-6 text-green-500 mr-2" />
                  <h4 className="text-lg font-medium text-gray-900 dark:text-white">
                    {document.created_by_user_id ? 'Transfer Document' : 'Assign User'}
                  </h4>
                </div>

                {/* Show notice for unassigned documents */}
                {!document.created_by_user_id && (
                  <div className="bg-lex-accent-soft border border-lex-accent rounded-md p-3">
                    <div className="flex">
                      <ExclamationTriangleIcon className="h-5 w-5 text-lex-accent mr-2 mt-0.5" />
                      <div className="text-sm text-lex-accent">
                        <p className="font-medium">Document Assignment</p>
                        <p className="mt-1">This document is currently unassigned. You'll be assigning it to a user for the first time.</p>
                      </div>
                    </div>
                  </div>
                )}

                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {document.created_by_user_id ? 'Transfer To' : 'Assign To'} <span className="text-red-500">*</span>
                  </label>
                  {loadingUsers ? (
                    <div className="flex items-center py-2 text-sm text-gray-500">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-lex-accent mr-2"></div>
                      Loading users...
                    </div>
                  ) : (
                    <select
                      value={selectedUser}
                      onChange={(e) => setSelectedUser(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-lex-accent focus:border-lex-accent dark:bg-gray-700 dark:text-white"
                    >
                      <option value="">Select a user...</option>
                      {users.map(user => (
                        <option key={user.user_id || user.id} value={user.user_id || user.id}>
                          {user.first_name} {user.last_name} ({user.email}) - {user.role}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {document.created_by_user_id ? 'Transfer' : 'Assignment'} Reason <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={transferReason}
                    onChange={(e) => setTransferReason(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-lex-accent focus:border-lex-accent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Select a reason...</option>
                    {DOCUMENT_TRANSFER_REASONS.map(reason => (
                      <option key={reason.value} value={reason.value}>
                        {reason.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Transfer Notes
                  </label>
                  <textarea
                    value={transferNotes}
                    onChange={(e) => setTransferNotes(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-lex-accent focus:border-lex-accent dark:bg-gray-700 dark:text-white"
                    placeholder="Additional notes about this transfer..."
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="clientNotified"
                    checked={clientNotified}
                    onChange={(e) => setClientNotified(e.target.checked)}
                    className="form-checkbox h-4 w-4 text-lex-accent focus:ring-lex-accent border-gray-300 dark:border-gray-600 rounded"
                  />
                  <label htmlFor="clientNotified" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                    Client has been notified of this document transfer
                  </label>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          {selectedOption && (
            <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                onClick={selectedOption === 'archive' ? handleArchiveSubmit : handleTransferSubmit}
                disabled={
                  isSubmitting ||
                  (selectedOption === 'archive' && !archiveReason) ||
                  (selectedOption === 'transfer' && (!selectedUser || !transferReason))
                }
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-lex-accent text-base font-medium text-lex-accent-contrast hover:bg-lex-accent-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-lex-accent sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {selectedOption === 'archive' ? 'Archiving...' : 'Transferring...'}
                  </>
                ) : (
                  selectedOption === 'archive' ? 'Archive Document' : 'Transfer Document'
                )}
              </button>
              <button
                onClick={handleClose}
                disabled={isSubmitting}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-600 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-lex-accent sm:mt-0 sm:w-auto sm:text-sm"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Assignment Confirmation Modal */}
      {showAssignConfirmation && (
        <div className="fixed inset-0 z-[60] overflow-y-auto">
          <div className="flex items-center justify-center min-h-full p-4">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity lex-overlay-enter"
              onClick={() => setShowAssignConfirmation(false)}
            />

            <div className="relative w-full max-w-lg bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl lex-modal-enter">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-lex-accent-soft sm:mx-0 sm:h-10 sm:w-10">
                    <UserIcon className="h-6 w-6 text-lex-accent" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                      Assign User to Document
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        This document is currently <strong>unassigned</strong>. Would you like to assign it to{' '}
                        <strong>
                          {users.find(u => (u.user_id || u.id) === selectedUser)?.first_name}{' '}
                          {users.find(u => (u.user_id || u.id) === selectedUser)?.last_name}
                        </strong>?
                      </p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                        This will be recorded as an initial assignment, not a transfer.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={handleConfirmAssignment}
                  disabled={isSubmitting}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-lex-accent text-base font-medium text-lex-accent-contrast hover:bg-lex-accent-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-lex-accent sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Assigning...
                    </>
                  ) : (
                    'Assign User'
                  )}
                </button>
                <button
                  onClick={() => setShowAssignConfirmation(false)}
                  disabled={isSubmitting}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-600 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-lex-accent sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArchiveDocumentModal;
